import React, {useState, useEffect, useRef} from "react";
import GenericDialog from "@common/GenericDialog";
import {Button, Radio} from "antd";
import {TrailStepIcon} from "./TrailBuilder";
import axios from "@common/axios";
import Alert from "@common/Alert";

const SURVEY_OPTIONS = Object.freeze({
    band_1: Object.freeze({
        value: "1",
        label: "Not confident",
        range: [0, 25],
        sequence: 0,
        editable: false
    }),
    band_2: Object.freeze({
        value: "2",
        label: "Somewhat confident",
        range: [25, 50],
        sequence: 1,
        editable: true
    }),
    band_3: Object.freeze({
        value: "3",
        label: "Confident",
        range: [50, 75],
        sequence: 2,
        editable: true
    }),
    band_4: Object.freeze({
        value: "4",
        label: "Very confident",
        range: [75, 100],
        sequence: 3,
        editable: true
    })
});
const surveySegmentOptions = Object.entries(SURVEY_OPTIONS).map(([option, value]) => ({
    key: value.value,
    label: value.label,
    value: value.value,
    disabled: !value.editable,
    className: "ch-overflow-ellipse"
}));

const EditSurveyDecisionPointDialog = props => {
    const {open, data, readOnly, onClose} = props;
    const [title, setTitle] = useState("");
    const [activePathId, setActivePathId] = useState(null);
    const [warning, setWarning] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paths, setPaths] = useState({});
    const radioGroupRef = useRef();

    useEffect(() => {
        const currentPath = data.pathList.find(path => path.steps.find(step => step.stepId === data.stepId));
        const currentStep = currentPath.steps.find(step => step.stepId === data.stepId);
        const nextStep = currentPath.steps.find(step => step.stepSeq === (currentStep?.stepSeq + 1));
        let newPathExists = false;
        let dpPaths = {
            [currentPath.pathId]: {
                nextStep: nextStep,
                band: SURVEY_OPTIONS.band_1,
                editable: false
            }
        };
        data.pathList
            .filter(path => path.startStepId === data.stepId)
            .map(path => {
                if (path.pathId <= 0) newPathExists = true;
                const band = Object.values(SURVEY_OPTIONS).find(option => option.range[0] <= path.percentage && option.range[1] >= path.percentage);
                dpPaths[path.pathId] = {
                    nextStep: path.steps[0],
                    band: band,
                    editable: true
                };
            });
        if (data.newPath && !newPathExists) {
            if (Object.keys(dpPaths).length < Object.values(SURVEY_OPTIONS).length) {
                const openBands = Object.values(SURVEY_OPTIONS).filter(option => !Object.values(dpPaths).some(path => path.band.value === option.value))
                const newBand = openBands[0];
                dpPaths[0] = {
                    nextStep: {pathwayActivityIcon: "help", pathwayActivityName: "New path"},
                    band: newBand,
                    editable: true
                }
            } else {
                setWarning("Maximum number of paths reached");
            }
        }
        setTitle(currentStep.pathwayActivityName + " step: result paths");
        setPaths(calculatePathDisplayGaps(dpPaths));
    }, [open]);

    useEffect(() => {
        if (!activePathId) return;

        const radios = radioGroupRef.current?.querySelectorAll('input[type="radio"]');
        radios[paths?.[activePathId]?.band.sequence].focus();
    }, [activePathId]);

    const calculatePathDisplayGaps = (updatePaths) => {
        let dpPaths = {...updatePaths};
        let gaps = [];
        Object.values(dpPaths)
            .sort((a, b) => parseFloat(a.band?.sequence) - parseFloat(b.band?.sequence))
            .map((path, i) => {
                if (i > 0) gaps[i - 1][1] = path.band.range[0];
                gaps.push([...path.band.range]);
            });
        gaps[gaps.length - 1][1] = 100;
        Object.entries(dpPaths)
            .sort(([, aPath], [, bPath]) => parseFloat(aPath.band.sequence) - parseFloat(bPath.band.sequence))
            .map(([pathId, path], index) => {
                dpPaths[pathId].gap = gaps[index];
            });
        return dpPaths;
    }

    const handleUpdateSurveyOption = value => {
        if (!activePathId) return;

        const sourcePath = paths[activePathId];
        const sourceBand = sourcePath.band;
        const newBand = Object.values(SURVEY_OPTIONS).find(option => option.value === value);

        if (!sourcePath.editable || !newBand.editable || (sourceBand.value === newBand.value)) return;

        let newDpPaths = { ...paths };
        const pathOnBand = Object.values(paths).find(path => path.band.value === value);
        if (pathOnBand) {
            // Band is occupied, so we need to move the path to the new band.
            const direction = newBand.sequence > sourceBand.sequence ? 1 : -1;
            for (let i = sourceBand.sequence; i !== newBand.sequence; i += direction) {
                const bandToMoveTo = Object.values(SURVEY_OPTIONS).find(opt => opt.sequence === i);
                const [pathIdToShift] = Object.entries(newDpPaths).find(
                    ([id, p]) => p.band?.sequence === i + direction
                ) || [];
                if (pathIdToShift) {
                    newDpPaths[pathIdToShift] = {...newDpPaths[pathIdToShift], band: bandToMoveTo};
                }
            }
        }
        newDpPaths[activePathId] = { ...newDpPaths[activePathId], band: newBand };
        setPaths(calculatePathDisplayGaps(newDpPaths));
    }

    return (
        <GenericDialog
            title={title}
            contentStyle={{
                width: 600
            }}
            open={open}
            onClose={() => {
                onClose();
            }}
            buttons={
                !readOnly ?
                    [{
                        name: "Update",
                        callback: async () => {
                            if (isSubmitting || readOnly) return;

                            try {
                                setIsSubmitting(true);

                                let updatedPathsCount = 0;
                                let params = {
                                    stepId: data.stepId
                                }
                                let newPathObj = null;

                                Object.entries(paths).forEach(([pathIdStr, path]) => {
                                    const pathId = parseInt(pathIdStr);
                                    const newPercentage = path.band.range[0] + Math.round((path.band.range[1] - path.band.range[0]) / 2);
                                    if (pathId > 0) {
                                        const thisPathPercentage = data.pathList.find(plPath => plPath.pathId === parseInt(pathId)).percentage;
                                        if (!(path.band.range[0] <= thisPathPercentage && path.band.range[1] > thisPathPercentage)) {
                                            const prefix = "paths[" + updatedPathsCount++ + "]";
                                            params[prefix + ".pathId"] = pathId;
                                            params[prefix + ".percentage"] = thisPathPercentage;
                                            params[prefix + ".updatePercentage"] = newPercentage;
                                        }
                                    } else {
                                        newPathObj = {
                                            pathId: data.pathId,
                                            stepId: data.stepId,
                                            completionPercentage: newPercentage
                                        };
                                    }
                                });

                                let response;
                                if (updatedPathsCount > 0) {
                                    response = await axios.post("/trail/updatePathDecisionPoint.action", params, {
                                        headers: { "Content-Type": "application/x-www-form-urlencoded"},
                                    });
                                } else if (newPathObj != null) {
                                    response = await axios.post("/trail/createBranchPath.action", newPathObj, {
                                        headers: { "Content-Type": "application/x-www-form-urlencoded"},
                                    });
                                } else {
                                    return;
                                }
                                if (response.data?.success) {
                                    data.onUpdateSuccess(response.data);
                                    onClose();
                                } else {
                                    console.error("Update unsuccessful:", response.data);
                                    displayToastMessage("Update failed", null, "error", undefined, 4000);
                                }
                            } finally {
                                setIsSubmitting(false);
                            }
                        },
                    }]: [{
                        name: 'Cancel',
                        extraProps: { type: 'text' },
                    }]
            }
        >
            <ul>
                <li>First select a path to update.</li>
                <li>Then choose a survey level requirement for users to branch onto the new path</li>
                <li>The first option/value cannot be changed because it represents the original path</li>
            </ul>
            {warning && (
                <Alert message={warning} type="warning" role="alert" />
            )}
            <div className="flexColumn margin-t--l">
                <div className="ch-emph-block padding--m" style={{overflow: "hidden"}}>
                    <div className="flexRow extraSmallGap">
                        <div className="flex ch-pw-ani" style={{flex: `0 0 ${Object.values(paths).find(path => path.band.sequence === 0)?.gap[1] ?? 0}%`}}>
                            <label className="ch-input-label">Main path</label>
                        </div>
                        <div className="flex ch-pw-ani">
                            <label className="ch-input-label">Branch paths</label>
                        </div>
                    </div>
                    <div className="flexRow extraSmallGap">
                        {Object.entries(paths)
                            .sort(([, aPath], [, bPath]) => parseFloat(aPath.band.sequence) - parseFloat(bPath.band.sequence))
                            .map(([pathId, path], index) => {
                                return (
                                    <Button
                                        key={pathId}
                                        disabled={!path.editable}
                                        className={`margin--rm padding--rm ch-pw-ani ch-pw-dp-rangeBlk${activePathId === pathId ? " ch-pw-dp-rangeBlk-selected" : ""}`}
                                        style={{
                                            flex: `.${path.gap[1] - path.gap[0]} 0 0%`,
                                        }}
                                        onClick={() => setActivePathId(pathId)}
                                    >
                                        <TrailStepIcon
                                            icon={path.nextStep?.pathwayActivityIcon}
                                            title={path.nextStep?.pathwayActivityName}
                                            style={{
                                                transform: `translateY(-${12/2}px)`,
                                            }}
                                        />
                                    </Button>
                                )
                            })
                        }
                    </div>
                </div>
                <div className="padding-x--m">
                    <Radio.Group
                        ref={radioGroupRef}
                        className="margin-t--m"
                        block
                        optionType="button"
                        buttonStyle="solid"
                        value={paths?.[activePathId]?.band.value ?? null}
                        disabled={readOnly || !activePathId}
                        options={surveySegmentOptions}
                        onChange={event => handleUpdateSurveyOption(event.target.value)}
                    />
                </div>
            </div>
        </GenericDialog>
    )
}

export default EditSurveyDecisionPointDialog;